import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, find, flatMap, forEach, includes, map, reduce, sortBy, uniq } from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { unsubscribeComponent } from '../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ICSVUploadErrors } from '../../../shared/modals/csv-upload/error-modal/csv-upload-error-modal.component';
import { ModalsService } from '../../../shared/modals/modals.service';
import { CsvImporterService } from '../../../shared/services/csv-importer/csv-importer-service';
import { DateHelpers } from '../../../shared/services/date-helpers/date-helpers.service';
import { UtilitiesService } from '../../../shared/services/utilities/utilities.service';
import {
  ICsvCollegeCreatePayload,
  ICsvCollegeUpdatePayload,
  IDataLoad,
} from '../../../shared/typings/interfaces/data-load.interface';
import { ICollegePath } from '../../../shared/typings/interfaces/paths.interface';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { IStudentPath } from '../../../shared/typings/interfaces/studentPaths.interface';
import {
  FINAL_DECISION_STATUSES,
  TValidStudentPathStatuses,
  VALID_PATH_STATUSES,
} from '../../../shared/typings/types/student-path.types';
import { CreateDataLoad } from '../../../store';
import { COLLEGE_LIST_CSV_COLUMNS, VALID_COLLEGE_HEADERS } from './college-list-upload.constant';
import {
  ICollegeListGrouping,
  ICollegeListRow,
  ICollegeListUploadGroupings,
  ICollegeNamePendingGrouping,
  IParseCollegeListCsvPayload,
  IStudentIdMap,
  IStudentIdPendingGrouping,
  IUploadedCollege,
} from './college-list-upload.interface';

// example:
// {
//   'student123': {
//     'College Name': {
//       status: 'Plans to apply',
//       studentPathId: 'studentPath123'
//     }
//   }
// } (JE)
export interface IStudentIdCollegeMap {
  [key: string]: { [key: string]: { status: TValidStudentPathStatuses; studentPathId: string; pathId: string } };
}

interface IUploadHistoryRow {
  uploadName: string;
  userName: string;
  date: string;
}

export interface ICollegeNameMap {
  [key: string]: ICollegePath;
}

export interface ICollegeListUploadPayload {
  errors: ICSVUploadErrors;
  create: ICsvCollegeCreatePayload[];
  update: ICsvCollegeUpdatePayload[];
  pending?: {
    studentId?: IStudentIdPendingGrouping;
    collegeName?: ICollegeNamePendingGrouping;
  };
  studentIdCollegeMap?: IStudentIdCollegeMap;
  collegePathsMap?: ICollegeNameMap;
}

@unsubscribeComponent
@Component({
  selector: 'college-list-upload',
  template: require('./college-list-upload.component.html'),
  styles: [require('./college-list-upload.component.scss')],
})
export class CollegeListUploadComponent implements OnInit {
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;
  @Input() studentPaths$: Observable<IStudentPath[]>;
  @Input() collegePaths$: Observable<ICollegePath[]>;
  @Input() dataLoads$: Observable<IDataLoad[]>;
  @Input() currentSchool: ISchool;
  @Input() students$: Observable<IStudent[]>;

  uploadsHistory: IUploadHistoryRow[];
  infoText = 'Upload a CSV file to modify student college lists and application statuses in bulk.';
  requirementsLink = 'https://docs.google.com/document/d/1K3HmZTG4_8MmohYCusaoim1SYztHd_Ujv-Bk3FB94bg';

  dataSubscription: Subscription;
  studentPaths: IStudentPath[];
  collegePaths: ICollegePath[];
  students: IStudent[];
  displayBoxShadow = false;

  constructor(
    @Inject('$window') public $window: any,
    private dateHelpers: DateHelpers,
    private csvImporterService: CsvImporterService,
    private modalsService: ModalsService,
    public store: Store<any>,
    public utilitiesService: UtilitiesService,
  ) {
    //
  }

  ngOnInit() {
    this.uploadsHistory = this.formatHistory(this.uploadsHistory);

    this.dataSubscription = combineLatest(this.studentPaths$, this.collegePaths$, this.students$, this.dataLoads$)
      .pipe(
        tap(([studentPaths, collegePaths, students, dataLoads]) => {
          this.studentPaths = studentPaths;
          this.collegePaths = collegePaths;
          this.students = students;
          const sortedDataLoads = sortBy(dataLoads, dataLoad => dataLoad.createdAt).reverse();
          const filteredDataLoads = filter(sortedDataLoads, dataLoad => dataLoad.context === 'COLLEGE_LIST');
          this.uploadsHistory = map(filteredDataLoads, (dataLoad: IDataLoad) => {
            const {
              createdBy: { firstName, lastName },
              createdAt,
              metaData: {
                changes: { studentIds },
              },
            } = dataLoad;
            const date = this.dateHelpers.getFormattedMoment(createdAt, 'MMM DD, YYYY');
            const count = studentIds.length;
            const studString = count === 1 ? 'student' : 'students';
            const uploadName = `Data upload - ${count} ${studString} updated`;
            return { uploadName, userName: `${firstName} ${lastName}`, date };
          });
        }),
      )
      .subscribe();
  }

  public openUpload(): void {
    this.fileInput.nativeElement.click();
  }

  public formatHistory(history) {
    return map(history, item => {
      const {
        name,
        uploadedBy: { first, last },
        createdAt,
      } = item;
      const fullName = `${first} ${last}`;
      const date = this.dateHelpers.getFormattedFromISO(createdAt, 'MMM DD, YYYY');
      return {
        uploadName: name,
        userName: fullName,
        date,
      };
    });
  }

  async handleFileInput(files: FileList) {
    if (files.length) {
      const fileToUpload = files[0];
      const { size, name } = fileToUpload;
      // check file size
      const mbs = size / 1000000; // convert bytes to MB
      // using es2018 "dotAll", this regex would be `/\..*\.csv$/s`. However, our babel config does not currently support this es2018 feature.
      // hence, the use of unicode for the regex instead. (Carlos)
      const doubleExtensionRegex = /\.[\0-\uFFFF]*\.csv$/;
      // check for double extensions
      const doubleExtension = name.match(doubleExtensionRegex); // regex checks for string ending in .****.csv
      if (mbs > 5 || doubleExtension) {
        const errors = { format: [] };
        if (mbs > 5) errors.format = [...errors.format, `File size (${mbs}MB) exceeds limit of 5MB`];
        if (doubleExtension) errors.format = [...errors.format, `File name includes a double extension`];
        this._createDataLoad({ errors, create: [], update: [] });
      } else {
        const text = await this.csvImporterService.readCsv(fileToUpload);
        const columns = COLLEGE_LIST_CSV_COLUMNS;
        const headerCase = 'ignore';
        const parsedJson = this.csvImporterService.csvToJson(text, columns, headerCase);
        const payload = {
          rows: parsedJson as ICollegeListRow[],
          schoolId: this.currentSchool._id,
          studentPaths: this.studentPaths,
          collegePaths: this.collegePaths,
          text,
        };
        const parsedData = this.parseCollegeListCsv(payload);
        const create = this._shapeCreatePayload(parsedData.toCreate);
        const update = this._shapeUpdatePayload(parsedData.toUpdate);
        const { pending } = parsedData;

        // get count of total # of changes, add error when > 10000 total changes (JE)
        const recordCount = create.length + update.length;
        const { errors } = parsedData;
        if (recordCount > 10000)
          errors.recordLimit = [`Count of changes (${recordCount}) exceeds the maximum upload limit`];
        // check for errors to reject the upload
        const {
          errors: { studentId, collegeName, status, format, rows, recordLimit },
        } = parsedData;
        if (
          studentId.length ||
          collegeName.length ||
          status.length ||
          format.length ||
          rows.length ||
          recordLimit.length
        ) {
          this._createDataLoad({ errors: parsedData.errors, create, update, pending });
        } else {
          // open confirmation modal
          const collegeCreates = Object.keys(parsedData.toCreate);
          const collegeUpdates = Object.keys(parsedData.toUpdate);
          const studentCount = uniq([...collegeCreates, ...collegeUpdates]).length;
          const data = {
            title: 'College list upload',
            recordCount: studentCount,
            parsedData,
          };
          /* istanbul ignore next */
          this.modalsService
            .openCsvConfirmModal(data)
            .afterClosed()
            .subscribe((confirmed: false | true) => {
              if (confirmed) this._createDataLoad({ errors: {}, create, update });
            });
        }
      }
    }
    // allows same file to be selected again (JE)
    this.fileInput.nativeElement.value = null;
  }

  openFreshDesk() {
    this.$window.FreshWidget.show();
  }

  parseCollegeListCsv(payload: IParseCollegeListCsvPayload): ICollegeListUploadGroupings {
    const { schoolId, rows, studentPaths, collegePaths, text } = payload;
    // check csv for minimum required format (JE)
    const headers = text
      .split('\n')[0]
      .split(',')
      .map(header => header.trim().toLowerCase());
    const formatErrors = this._validateFormat(headers);

    // map studentPaths to an object for quicker lookups
    const existingCollegesByStudentId = this._getExistingCollegesByStudentId(studentPaths);
    const studentIdMap = this._getStudentIdMap(this.students);
    const collegePathsMap = this._getCollegePathsMap(collegePaths);

    const result: ICollegeListUploadGroupings = {
      toCreate: {},
      toUpdate: {},
      pending: {
        studentId: {},
        collegeName: {},
      },
      errors: {
        studentId: [],
        collegeName: [],
        status: [],
        format: formatErrors,
        rows: [],
        recordLimit: [],
      },
    };
    if (formatErrors && formatErrors.length) {
      return result;
    }

    return reduce(
      rows,
      (res, row) => {
        // get all schools and statuses for this student;
        const schools = this._getSchoolsAndStatusArray(row);
        const studId = row.student_id ? `${row.student_id}${schoolId}` : null;
        const studentName = row.student_name;
        const parsedId = studId ? studId : '-';
        const parsedName = studentName ? studentName : '-';
        if (schools.length === 0) {
          // ignore rows with no colleges
          return res;
        } else {
          const idName = `${studId}${studentName}`; // creates unique combinations of input names and ids
          if (!studentName) {
            res.errors.rows = [
              ...res.errors.rows,
              `Incomplete data - id: ${parsedId}, name: ${parsedName}, schoolCount: ${schools.length}`,
            ];
          } else if (!studId || !studentIdMap[studId]) {
            // check for invalid studentId
            const error = !studId
              ? `Incomplete data - id: ${parsedId}, name: ${parsedName}, schoolCount: ${schools.length}`
              : `Invalid student id: ${studId}`;
            res.errors.studentId = [...res.errors.studentId, error];
            // add student row data to pending data for user to fix
            if (!res.pending.studentId[idName]) {
              res.pending.studentId[idName] = {
                schools: [],
                name: studentName,
                _id: studId,
                providedId: row.student_id,
              };
            }
            const updatedSchools = [...res.pending.studentId[idName].schools, ...schools];
            res.pending.studentId[idName] = {
              name: studentName,
              schools: updatedSchools,
              _id: studId,
              providedId: row.student_id,
            };
          }

          // check for multiple colleges with final decision status
          const finalDecisionStatuses = filter(schools, school => {
            const { status } = school;
            if (!status) return false;
            else return includes(FINAL_DECISION_STATUSES, status.trim());
          });
          if (finalDecisionStatuses.length > 1) res.errors.status = [...res.errors.status, studId];

          forEach(schools, school => {
            const { name, status } = school;
            // csv parsing adds space to last column, lowercase to account for user input with incorrect casing (JE)
            const trimmedName = name.trim();
            const lowerTrimmedName = trimmedName.toLowerCase();
            // check if college name is valid
            const collegePath = collegePathsMap[lowerTrimmedName];
            if (!collegePath) {
              res.errors.collegeName = [...res.errors.collegeName, trimmedName];
              if (!res.pending.collegeName[trimmedName]) res.pending.collegeName[trimmedName] = { studentIds: [] };
              const studentIds = [
                ...res.pending.collegeName[trimmedName].studentIds,
                { entered: row.student_id, full: studId, fullWithName: idName },
              ];
              res.pending.collegeName[trimmedName] = { studentIds };
            }
            // check if status is valid
            const validStatus = includes(VALID_PATH_STATUSES, status);
            if (!validStatus) res.errors.status = [...res.errors.status, status];

            if (collegePath && validStatus) {
              // check if student already has a school with this name;
              const studentColleges = existingCollegesByStudentId[studId];
              /* istanbul ignore next */
              if (studentColleges && studentColleges[lowerTrimmedName] && studentName) {
                // student already has active college assignment
                const college = studentColleges[lowerTrimmedName];
                const statusChange = college.status !== status && status !== 'Plans to apply';
                if (statusChange && !res.toUpdate[studId]) res.toUpdate[studId] = {};
                if (statusChange) res.toUpdate[studId][college.studentPathId] = { name: trimmedName, status };
              } else if (studentIdMap[studId] && studentName) {
                // college needs to be assigned to student
                if (!res.toCreate[studId]) res.toCreate[studId] = {};
                res.toCreate[studId][collegePath._id] = { name: trimmedName, status };
              }
            }
          });
          return res;
        }
      },
      result,
    );
  }

  _getExistingCollegesByStudentId(studentPaths: IStudentPath[]): IStudentIdCollegeMap {
    return reduce(
      studentPaths,
      (res, studPath) => {
        const {
          _id,
          path: { type, name, pathId },
          status,
          studentId,
        } = studPath;
        const isActive = status !== 'DELETED' && status !== 'CLOSED';
        if (type === 'COLLEGE' && isActive) {
          if (!res[studentId]) res[studentId] = {};
          const trimmedName = name.trim().toLowerCase(); // temporary fix to handle studentPaths with names that have trailing spaces
          res[studentId][trimmedName] = { status, studentPathId: _id, pathId };
        }
        return res;
      },
      {},
    );
  }

  _getStudentIdMap(students: IStudent[]): IStudentIdMap {
    return reduce(
      students,
      (res, student) => {
        res[student._id] = true;
        return res;
      },
      {},
    );
  }

  _getCollegePathsMap(collegePaths: ICollegePath[]): ICollegeNameMap {
    return reduce(
      collegePaths,
      (res, college) => {
        // lowercase to account for user input with incorrect cases (JE)
        const trimmedName = college.name.trim().toLowerCase();
        res[trimmedName] = college;
        return res;
      },
      {},
    );
  }

  _getSchoolsAndStatusArray(row: ICollegeListRow): IUploadedCollege[] {
    const schools: IUploadedCollege[] = [];
    for (let i = 1; i <= 20; i++) {
      const key = `school_#${i}`;
      const statusKey = `school_#${i}_status`;
      const schoolName = row[key];
      if (schoolName && schoolName.length > 1) {
        const statusVal = row[statusKey];
        const schoolStatus = statusVal && statusVal.length > 1 ? statusVal.trim() : 'Plans to apply';
        schools.push({ name: schoolName, status: schoolStatus });
      }
    }
    return schools;
  }

  _validateFormat(headers: string[]): string[] {
    const response = [];
    const headersCount = reduce(
      headers,
      (res, header) => {
        // upload must have at least one school column (JE)
        // regex matches 'School #1' through 'School #20'
        if (header.match(/^school #(20|1[0-9]|[1-9])$/)) res.hasSchool = 1;
        if (!res[header]) res[header] = 0;
        res[header] = res[header] + 1;
        return res;
      },
      { hasSchool: 0 },
    );
    const validStudentIDCol = headersCount['student id'] > 0;
    if (!validStudentIDCol) response.push('Student ID column is missing');
    const validStudentNameCol = headersCount['student name'] > 0;
    if (!validStudentNameCol) response.push('Student Name column is missing');
    const validSchoolCol = headersCount.hasSchool;
    if (!validSchoolCol) response.push('School # column is missing');
    const duplicateHeaders = find(Object.keys(headersCount), header => {
      // only check for duplicate headers from our list of expected headers (JE)
      return headersCount[header] > 1 && VALID_COLLEGE_HEADERS[header];
    });
    if (duplicateHeaders) response.push('Duplicate headers');
    return response;
  }

  _shapeCreatePayload(toCreate: ICollegeListGrouping): ICsvCollegeCreatePayload[] {
    const studentIds = Object.keys(toCreate);
    return flatMap(studentIds, studentId => {
      const studentPayload = toCreate[studentId];
      const collegePathIds = Object.keys(studentPayload);
      return map(collegePathIds, pathId => {
        return { studentId, pathId, status: studentPayload[pathId].status };
      });
    });
  }

  _shapeUpdatePayload(toUpdate: ICollegeListGrouping): ICsvCollegeUpdatePayload[] {
    const studentIds = Object.keys(toUpdate);
    return flatMap(studentIds, studentId => {
      const studentPayload = toUpdate[studentId];
      const studentPathIds = Object.keys(studentPayload);
      return map(studentPathIds, studentPathId => {
        return { studentId, studentPathId, status: studentPayload[studentPathId].status };
      });
    });
  }

  _createDataLoad(payload: ICollegeListUploadPayload) {
    const {
      errors: { studentId, collegeName },
      create,
      update,
      pending,
    } = payload;
    const parsedStudentIdErrors = studentId ? studentId : [];
    const parsedCollegeNameErrors = collegeName ? collegeName : [];
    const combinedErrors = reduce(
      Object.keys(payload.errors),
      (res, key) => {
        const keyErrors = payload.errors[key];
        res = [...res, ...keyErrors];
        return res;
      },
      [],
    );
    const parsedCreate = combinedErrors.length ? [] : create;
    const parsedUpdate = combinedErrors.length ? [] : update;
    const dataLoadPayload = {
      schoolId: this.currentSchool._id,
      errors: combinedErrors,
      type: 'CSV_UPLOAD',
      context: 'COLLEGE_LIST',
      toCreate: parsedCreate,
      toUpdate: parsedUpdate,
      pending,
    };
    // dispatch action to create data load
    this.store.dispatch(new CreateDataLoad(dataLoadPayload));
    // check if csv fixing modal can be opened
    const fixableErrors = parsedStudentIdErrors.length + parsedCollegeNameErrors.length;
    const rejectionErrors = combinedErrors.length - fixableErrors;
    if (!rejectionErrors && fixableErrors) {
      // this map is needed to determine whether a path should be created or updated after
      // the user fixes id errors (JE)
      payload.studentIdCollegeMap = this._getExistingCollegesByStudentId(this.studentPaths);
      payload.collegePathsMap = this._getCollegePathsMap(this.collegePaths);
      this.modalsService
        .openCsvUploadsFixErrorsModal(payload)
        .afterClosed()
        .subscribe((fixedRows: { toCreate: ICsvCollegeCreatePayload[]; toUpdate: ICsvCollegeUpdatePayload[] }) => {
          /* istanbul ignore next */
          if (fixedRows) {
            const combinedCreate = [...create, ...fixedRows.toCreate];
            const combinedUpdate = [...update, ...fixedRows.toUpdate];
            const studentCount = uniq(map([...combinedCreate, ...combinedUpdate], change => change.studentId)).length;
            const data = {
              title: 'College list upload',
              recordCount: studentCount,
            };
            this.modalsService
              .openCsvConfirmModal(data)
              .afterClosed()
              .subscribe((confirmed: false | true) => {
                if (confirmed) this._createDataLoad({ errors: {}, create: combinedCreate, update: combinedUpdate });
              });
          }
        });
    } else if (rejectionErrors) {
      // more than just studentId errors are present
      this.modalsService.openCsvUploadsErrorModal(payload.errors, 'COLLEGE_LIST');
    }
  }

  onScroll(): void {
    const scrollTop = this.scrollContainer.nativeElement.scrollTop;
    this.displayBoxShadow = this.utilitiesService.showListPanelShadow(scrollTop);
  }
}
